// src/components/ui/Charts/PieChart.tsx
import React, { useMemo, forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Pie, PieChart as RechartsPieChart, Cell, Tooltip, Legend, ResponsiveContainer, PieLabel } from 'recharts';
import { COLORS } from '@/theme/colors';
import { CustomTooltip } from './CustomTooltip';
import { ChartRenderer } from './ChartRenderer';
import { formatPercent } from '@/utils/Formatters';
import styles from './PieChart.module.css';

export interface PieDataItem {
  name: string;
  value: number;
  color?: string;
}

export interface PieChartProps {
  // Основные данные и настройки
  data: PieDataItem[];
  dataKey?: string;
  nameKey?: string;
  
  // Базовые настройки отображения
  showLegend?: boolean;
  showLabels?: boolean;
  height?: number;
  className?: string;
  
  // Специфичные настройки для PieChart
  innerRadius?: number;
  outerRadius?: number;
  paddingAngle?: number;
  label?: boolean | PieLabel<any>;
  labelThreshold?: number;
  
  // Настройки данных
  valueType?: 'number' | 'percent' | 'currency' | 'compact';
  precision?: number;
  tooltipFormatter?: (value: any, name: string, props: any) => React.ReactNode;
  tooltip?: React.ReactElement<any> | ((props: any) => React.ReactNode);
  
  // Новые параметры для унифицированного управления
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'full' | 'auto';
  minHeight?: number;
  aspectRatio?: number;
  isLoading?: boolean;
  error?: string | null;
  noData?: boolean;
  title?: string;
  subtitle?: string;
  margin?: { top?: number; right?: number; bottom?: number; left?: number };
  
  // Колбэки и обработчики
  onClick?: (data: any, index: number) => void;
  onLegendClick?: (data: any) => void;
}

/**
 * Компонент для отображения круговых диаграмм (PieChart)
 * Поддерживает унифицированное управление размерами и состояниями через ChartRenderer
 */
export const PieChart = forwardRef<HTMLDivElement, PieChartProps>(({
  // Основные данные и настройки
  data,
  dataKey = 'value',
  nameKey = 'name',
  
  // Базовые настройки отображения
  showLegend = true,
  showLabels = true,
  height,
  className = '',
  
  // Специфичные настройки для PieChart
  innerRadius = 0,
  outerRadius = 80,
  paddingAngle = 0,
  label,
  labelThreshold = 5,
  
  // Настройки данных
  valueType = 'percent',
  precision = 1,
  tooltipFormatter,
  tooltip,
  
  // Новые параметры для унифицированного управления
  size = 'md',
  minHeight,
  aspectRatio,
  isLoading = false,
  error = null,
  noData = false,
  title,
  subtitle,
  margin = { top: 20, right: 30, bottom: 20, left: 30 },
  
  // Колбэки и обработчики
  onClick,
  onLegendClick
}, ref) => {
  useTranslation(['analytics', 'ui', 'common']);
  
  // Обратная совместимость для размеров
  const effectiveMinHeight = useMemo(() => {
    if (minHeight) return minHeight;
    if (height) return height;
    return undefined;
  }, [minHeight, height]);
  
  // Создаем палитру цветов на основе темы
  const defaultColors = [
    COLORS.primary,
    '#32a852',
    '#3273a8',
    '#a83232',
    '#a87832',
    '#8432a8',
    '#a83282',
    '#4b32a8',
    '#32a89b',
  ];
  
  // Определяем, нет ли данных
  const hasNoData = useMemo(() => {
    return noData || !data || data.length === 0;
  }, [noData, data]);
  
  // Рендерер для меток с проверкой порога отображения и с форматированием
  const renderThresholdLabel = useMemo(() => {
    return (props: any) => {
      const { cx, cy, midAngle, outerRadius, percent, payload } = props;
      
      // Не отображаем метку, если процент меньше порогового значения
      if (percent * 100 < labelThreshold) {
        return null;
      }
      
      // Выносные линии и метки для крупных сегментов
      const RADIAN = Math.PI / 180;
      const labelRadius = outerRadius * 1.2;
      
      const x = cx + labelRadius * Math.cos(-midAngle * RADIAN);
      const y = cy + labelRadius * Math.sin(-midAngle * RADIAN);
      
      const sin = Math.sin(-midAngle * RADIAN);
      const cos = Math.cos(-midAngle * RADIAN);
      
      const mx = cx + (outerRadius + 10) * cos;
      const my = cy + (outerRadius + 10) * sin;
      
      const color = payload.color;
      
      return (
        <g>
          {/* Выносная линия */}
          <path 
            d={`M${cx + outerRadius * cos},${cy + outerRadius * sin}L${mx},${my}L${x},${y}`} 
            stroke={color} 
            fill="none" 
            className={styles.pieLabelLine}
          />
          
          {/* Метка с процентом */}
          <text 
            x={x} 
            y={y} 
            fill={color}
            textAnchor={x > cx ? 'start' : 'end'} 
            dominantBaseline="central"
            className={styles.pieLabel}
          >
            {formatPercent(percent, precision)}
          </text>
        </g>
      );
    };
  }, [labelThreshold, precision]);
  
  // Обработчик клика на легенду
  const handleLegendClick = useMemo(() => {
    return onLegendClick;
  }, [onLegendClick]);
  
  return (
    <ChartRenderer
      size={size}
      minHeight={effectiveMinHeight}
      aspectRatio={aspectRatio}
      className={className}
      isLoading={isLoading}
      error={error}
      noData={hasNoData}
      title={title}
      subtitle={subtitle}
      ref={ref}
    >
      <ResponsiveContainer width="100%" height="100%">
        <RechartsPieChart 
          margin={margin} 
          onClick={onClick}
        >
          <Pie
            data={data}
            dataKey={dataKey}
            nameKey={nameKey}
            cx="50%"
            cy="50%"
            innerRadius={innerRadius}
            outerRadius={outerRadius}
            paddingAngle={paddingAngle}
            fill={COLORS.primary}
            label={showLabels ? (label || renderThresholdLabel) : undefined}
            className={styles.pie}
          >
            {data.map((entry, index) => (
              <Cell 
                key={`cell-${index}`} 
                fill={entry.color || defaultColors[index % defaultColors.length]} 
                className={styles.pieSegment}
              />
            ))}
          </Pie>
          
          <Tooltip 
            content={tooltip || (
              <CustomTooltip 
                valueType={valueType} 
                precision={precision} 
                formatter={tooltipFormatter} 
              />
            )}
          />
          
          {showLegend && (
            <Legend 
              onClick={handleLegendClick}
              verticalAlign="top"
              wrapperStyle={{ paddingBottom: '10px' }}
            />
          )}
        </RechartsPieChart>
      </ResponsiveContainer>
    </ChartRenderer>
  );
});

PieChart.displayName = 'PieChart';

export default PieChart;